import { computed, Directive, ElementRef, HostListener, Renderer2, input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
@Directive({
    selector: 'input[currencyInput]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: CurrencyInputDirective,
            multi: true
        }
    ],
    standalone: true
})
export class CurrencyInputDirective implements ControlValueAccessor {

  // Digit limitation properties from special-number-input directive
  maxIntegerDigits = input<number | null>(null);
  decimalPoints = input<number>(2);
  maxLength = computed(() => {
    if(this.maxIntegerDigits()) {
      return this.maxIntegerDigits() + this.decimalPoints()    
    } 
     
    return null;
  });

  constructor(private el: ElementRef, private renderer: Renderer2) { }

  private value: any;

  // Implement ControlValueAccessor interface
  // These methods will be called by the form control
  writeValue(value: any): void {
    this.value = value;
    const formattedValue = (this.value !== null && this.value !== undefined && this.value !== '') ? new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(parseFloat(this.value)) : '';
    this.renderer.setProperty(this.el.nativeElement, 'value', formattedValue);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.el.nativeElement, 'disabled', isDisabled);
  }

  // Keydown handler for digit limitations
  @HostListener('keydown', ['$event'])
  onKeyDown(e: KeyboardEvent) {
    let inputValue = (e.target as HTMLInputElement).value;
    if (this.checkAllowedKeys(e)) {
      return;
    }
    if (isNaN(Number(e.key))) {
      e.preventDefault();
    }
    if (this.checkTextSelection()) {
      return;
    }
    if (this.checkForMaxLength(inputValue)) {
      e.preventDefault();
    }
  }


  // Listen for input changes - handles all formatting logic
  @HostListener('input', ['$event.target.value'])
  onInput(value: string): void {
    // Skip formatting for incomplete decimal inputs
    if(!value.endsWith('.') && !value.endsWith('.0') && !value.match('[.][\\d][0]')){    
      this.formatAndSetValue(value);
    }
  }

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  // Helper methods from special-number-input directive
  private allowedKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'Home',
    'End',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'Clear',
    'Control',
    ',',
    '.'
  ];

  private checkTextSelection() {
    return window.getSelection()!.toString().trim().length > 0;
  }

  private checkAllowedKeys(e: KeyboardEvent): boolean {
    const allKeys = [
      ...this.allowedKeys,
      this.decimalPoints() > 0 ? '.' : '',
    ];

    return (allKeys.indexOf(e.key) > -1 || (e.key === 'a' && e.ctrlKey) || (e.key === 'a' && e.metaKey) || (e.key === 'v' && e.ctrlKey));
  }

  private checkForMaxLength(inputValue: string): boolean {
    if (this.decimalPoints() > 0) {
      if(inputValue.includes('.')){
        if (inputValue.replace('.', '').replace(/\,/g, '').length === this.maxLength()) {
          return true;
        }
      }
      else {
        if (inputValue.replace('.', '').replace(/\,/g, '').length === this.maxIntegerDigits()) {
          return true;
        }
      }
    } else {
      if (inputValue.replace(/\,/g, '').length === this.maxLength()) {
        return true;
      }
    }
    return false;
  }

  private formatAndSetValue(inputValue: string): void {
    const unformattedValue = inputValue.replace(/[^0-9.-]/g, '');
    this.value = unformattedValue;
    this.onChange(this.value);
    
    const formattedValue = (this.value !== null && this.value !== undefined && this.value !== '') ? new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: this.decimalPoints(),
    }).format(this.value) : '';

    this.renderer.setProperty(this.el.nativeElement, 'value', formattedValue);
  }
}